/**
 * The run, in words: one line per stage with its progress, a cancel, errors
 * with a retry. After a refresh the file and the key are gone on purpose; the
 * stage store is not, so re-choosing the file resumes without re-spending.
 */
import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { db } from '../../store/db.js'
import { useAppState } from '../../store/appState.js'
import { startRun, WorkerRunError } from '../../worker/client.js'
import type { RunHandle } from '../../worker/client.js'
import type { WorkerStage } from '../../worker/protocol.js'
import styles from './Reconstruct.module.scss'

const LABELS: Readonly<Record<WorkerStage, string>> = {
  import: 'Reading the export',
  identity: 'Working out who is who',
  extract: 'Finding candidate commitments',
  verify: 'Checking each candidate against its own words',
  attribution: 'Checking who a promise binds',
  lineage: 'Linking related messages',
  openness: 'Judging what is still open',
  route: 'Placing items in sections',
  signals: 'Noticing unsent drafts and unanswered asks',
  attention: 'Ordering by what deserves attention',
  assemble: 'Assembling the ledger',
}

// The worker's stage order; the labels above enumerate every stage.
const STAGES = Object.keys(LABELS) as readonly WorkerStage[]

type StageState = {
  readonly done: number
  readonly total: number | null
  readonly complete: boolean
  readonly cached: boolean
}

type Progress = Partial<Record<WorkerStage, StageState>>

function describe(stage: WorkerStage, state: StageState | undefined): string {
  if (!state) return 'waiting'
  if (state.complete) return state.cached ? 'done, from the cache' : 'done'
  if (stage === 'import') return `${state.done} messages so far`
  return state.total === null ? `${state.done}` : `${state.done} of ${state.total}`
}

export function Reconstruct() {
  const params = useAppState((s) => s.params)
  const file = useAppState((s) => s.file)
  const apiKey = useAppState((s) => s.api_key)
  const completed = useAppState((s) => s.completed)
  const runAgain = useAppState((s) => s.runAgain)
  const resume = useAppState((s) => s.resume)
  const [progress, setProgress] = useState<Progress>({})
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const handle = useRef<RunHandle | null>(null)
  const runningFor = useRef<Blob | null>(null)

  useEffect(() => {
    if (!params || !file || !apiKey) return
    // StrictMode mounts twice in dev; one run per file object.
    if (runningFor.current === file && handle.current) return
    runningFor.current = file
    setError(null)
    setProgress({})
    const run = startRun(
      {
        mbox: file,
        me: params.me,
        cutoff: params.cutoff,
        window_days: params.window_days,
        api_key: apiKey,
        ...(params.model ? { model: params.model } : {}),
      },
      {
        onProgress: (m) =>
          setProgress((p) => ({
            ...p,
            [m.stage]: { done: m.done, total: m.total, complete: false, cached: false },
          })),
        onStageComplete: (m) =>
          setProgress((p) => ({
            ...p,
            [m.stage]: {
              done: p[m.stage]?.total ?? p[m.stage]?.done ?? 0,
              total: p[m.stage]?.total ?? null,
              complete: true,
              cached: m.cached,
            },
          })),
      },
    )
    handle.current = run
    run.done
      .then(async (done) => {
        const id = `run-${done.summary.model_id}-${params.cutoff}-${Date.now()}`
        await db().runs.put({
          id,
          cutoff: params.cutoff,
          window_days: params.window_days,
          me: params.me,
          completed_at: new Date().toISOString(),
          ledger: done.ledger,
        })
        handle.current = null
        completed(id)
      })
      .catch((err: unknown) => {
        handle.current = null
        runningFor.current = null
        setError(err instanceof WorkerRunError ? err.message : 'The run failed. Try again.')
      })
  }, [params, file, apiKey, attempt, completed])

  if (!params) return null

  if (!file || !apiKey) return <ResumeForm onResume={resume} />

  return (
    <section className={styles.root} aria-labelledby="reconstruct-title">
      <h1 id="reconstruct-title" className={styles.title}>
        Reconstructing
      </h1>
      <p className={styles.lede}>
        Reading the {params.window_days} days up to {params.cutoff}. This can take a while; the page
        can stay in the background.
      </p>
      <ol className={styles.stages} aria-live="polite">
        {STAGES.map((stage) => {
          const state = progress[stage]
          return (
            <li
              key={stage}
              className={`${styles.stage} ${state?.complete ? styles.isDone : ''} ${
                state && !state.complete ? styles.isActive : ''
              }`}
            >
              <span>{LABELS[stage]}</span>
              <span className={styles.status}>{describe(stage, state)}</span>
            </li>
          )
        })}
      </ol>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.actions}>
        {error ? (
          <button
            type="button"
            className={styles.primary}
            onClick={() => {
              runningFor.current = null
              setAttempt((n) => n + 1)
            }}
          >
            Try again
          </button>
        ) : (
          <button type="button" className={styles.quiet} onClick={() => handle.current?.cancel()}>
            Cancel
          </button>
        )}
        <button type="button" className={styles.quiet} onClick={runAgain}>
          Start over
        </button>
      </div>
    </section>
  )
}

function ResumeForm({
  onResume,
}: {
  readonly onResume: (input: { file: Blob; api_key: string }) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [key, setKey] = useState('')
  const runAgain = useAppState((s) => s.runAgain)
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (file && key.trim()) onResume({ file, api_key: key.trim() })
  }
  return (
    <form className={styles.root} onSubmit={submit}>
      <h1 className={styles.title}>Resume the reconstruction</h1>
      <p className={styles.lede}>
        The file and your key are not kept across a refresh. What was already computed is kept, so
        choosing the same file again continues where it left off without re-spending.
      </p>
      <div className={styles.field}>
        <label htmlFor="resume-mbox">The same Takeout export</label>
        <input
          id="resume-mbox"
          type="file"
          accept=".mbox"
          onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor="resume-key">Anthropic API key</label>
        <input
          id="resume-key"
          type="password"
          autoComplete="off"
          value={key}
          onChange={(e) => setKey(e.currentTarget.value)}
        />
      </div>
      <div className={styles.actions}>
        <button type="submit" className={styles.primary}>
          Resume
        </button>
        <button type="button" className={styles.quiet} onClick={runAgain}>
          Start over
        </button>
      </div>
    </form>
  )
}
